<template>
    <div class="tools-tab tools-tab-user"
         :class="[$onduro.hasModule('availability') ? 'has-availability' : '', item.tags.length ? 'is-filtered' : '']"
         v-loader.users="{ delay: 0 }"
    >
        <template v-if=" ! loading">
            <ul class="list" v-if=" ! applicants.length">
                <li v-for="user in users" :class="[hasUser(user) ? 'is-active' : '']" @click="select(user)">
                    <span v-if="$onduro.hasModule('availability')" :class="getAvailabilityClasses(user)" v-on:mouseover="showAvailability(user)" v-on:mouseout="hideAvailability()"></span>
                    <span>{{ user.name }}</span>
                    <icon name="delete" v-if="hasUser(user)" />
                    <icon name="add" v-else-if="multiple" />
                    <icon name="check" v-else />
                </li>
            </ul>
            <ul class="list" v-else>
                <li v-for="user in applicants" :class="[hasUser(user) ? 'is-active' : '', user.planned ? 'is-planned' : '']" @click="select(user)">
                    <span v-if="$onduro.hasModule('availability')" :class="getAvailabilityClasses(user)" v-on:mouseover="showAvailability(user)" v-on:mouseout="hideAvailability()"></span>
                    <span>{{ user.name }}</span>
                    <span class="applied-at" v-html="$t('page.roster.tools.applied_at', { date: humanDate(user.appliedAt) })"></span>
                    <icon name="delete" v-if="hasUser(user)" />
                    <icon name="add" v-else-if="multiple" />
                    <icon name="check" v-else />
                </li>
                <li v-for="(user, index) in remaining" :class="[hasUser(user) ? 'is-active' : '', index === 0 ? 'border-top' : '' ]" @click="select(user)" v-if="!$onduro.getConfig('restrict.planner')">
                    <span v-if="$onduro.hasModule('availability')" :class="getAvailabilityClasses(user)" v-on:mouseover="showAvailability(user)" v-on:mouseout="hideAvailability()"></span>
                    <span>{{ user.name }}</span>
                    <icon name="delete" v-if="hasUser(user)" />
                    <icon name="add" v-else-if="multiple" />
                    <icon name="check" v-else />
                </li>
            </ul>
            <div class="empty" v-if=" ! users.length">
                {{ $t('page.roster.tools.empty_users') }}
            </div>
        </template>
        <div class="filtered" v-show="item.tags.length">
            {{ $t('page.roster.tools.filtered_tags') }}
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import events from '../../../events/events';
import shared from '../../../pages/planning/shared';

const availabilitySorting = ['available', 'limited', 'vacation', 'unavailable'];
const weekDays = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

export default
{
    name: 'User',

    props: ['item'],

    data() {
        return {
            shared: shared.state,
            multiple: false,
            loading: false,
            available: [],
            applications: [],
        }
    },

    computed:
    {
        // only show users that match the item's duty
        users()
        {
            // no duty set? return all users!
            if ( ! this.item.duty) return this.sort(this.map(this.shared.users));

            return this.sort(this.map(this.filter(this.shared.users)));
        },

        applicants()
        {
            return this.sortApplications(this.mapApplications(this.filterApplications(this.shared.users)));
        },

        remaining()
        {
            return _.filter(this.users, user =>
            {
                return _.indexOf(_.map(this.applicants, 'id'), user.id) === -1
            });
        }
    },

    created()
    {
        this.getData()
    },

    watch:
    {
        item()
        {
            this.loading = true
            this.getData()
        }
    },

    methods:
    {
        getData()
        {
            return this.$http.all([this.fetchAvailable(), this.fetchApplications()]).then(() =>
            {
                this.$nextTick(() => { this.loading = false })
            })
        },

        fetchAvailable()
        {
            return new Promise((resolve, reject) =>
            {
                return this.$http.loader('users').get('/api/items/' + this.item.id + '/users')
                .then(response =>
                {
                    this.available = response.data;
                    resolve()
                })
                .catch(error =>
                {
                    reject(error);
                });
            });
        },

        humanDate(date)
        {
            return this.$options.filters.readableDate(date, 'YYYY-MM-DD HH:mm', 'DD. MMMM YYYY, HH:mm')
        },

        fetchApplications()
        {
            return new Promise((resolve, reject) =>
            {
                return this.$http.loader('users').get('/api/items/' + this.item.id + '/applications')
                .then(response =>
                {
                    this.applications = response.data;
                    resolve()
                })
                .catch(error =>
                {
                    reject(error);
                });
            });
        },

        getApplicationData(id)
        {
            return _.find(this.applications, application => application.membership.id === id)
        },

        filter(users)
        {
            let tags = this.item.tags;
            return _.filter(users, user =>
            {
                // user has not the required duty
                if (user.duties.indexOf(this.item.duty) == -1) return false;

                // user has not ALL of the required tag(s)
                if (tags.length && _.intersection(user.tags, tags).length != tags.length) return false;

                // user is not in available user list
                if (this.available.indexOf(user.id) == -1) return false;

                return true;
            });
        },

        filterApplications(users)
        {
            let tags = this.item.tags;
            let applicants = _.map(this.applications, 'membership.id');

            return _.filter(users, user =>
            {
                // user has not the required duty
                if (user.duties.indexOf(this.item.duty) == -1) return false;

                // user has not ALL of the required tag(s)
                if (tags.length && _.intersection(user.tags, tags).length != tags.length) return false;

                // user is not an applicant
                if (applicants.indexOf(user.membership) == -1) return false;

                return true;
            });
        },

        map(users)
        {
            if ( ! this.$onduro.hasModule('availability')) return users;

            return _.map(users, user =>
            {
                user.availability = this.getAvailability(user);

                return user;
            });
        },

        mapApplications(users)
        {
            if ( ! this.$onduro.hasModule('availability')) return users;

            return _.map(users, user =>
            {
                let applicationData = this.getApplicationData(user.membership);

                user.appliedAt = applicationData.appliedAt;
                user.accepted = applicationData.accepted;

                user.availability = this.getAvailability(user);

                // has user already been planned?
                user.planned = this.available.indexOf(user.id) == -1 ? true : false;

                return user;
            });
        },

        sort(users)
        {
            return _.sortBy(users, [user =>
            {
                if ( ! this.$onduro.hasModule('availability')) return 0;
                return user.availability.sorting;
            }, 'name']);
        },

        sortApplications(users)
        {
            return _.sortBy(users, ['accepted', user =>
            {
                if ( ! this.$onduro.hasModule('availability')) return 0;
                return user.appliedAt;
            }, 'name']);
        },

        // check if item has this user assigned
        hasUser(user)
        {
            return this.item.user == user.id;
        },

        select(user)
        {
            if (this.hasUser(user))
            {
                this.remove(user);
            }
            else
            {
                this.add(user);
            }
        },

        updateUserStats(item, stats)
        {
            events.$emit('recalculate', item.date);
            
            if ( ! item.assigned) return;

            let user = shared.getUser(item.user);

            user.stats = (stats) ? stats : item.stats;
        },

        add(user, params)
        {
            let data = {
                user: user.id
            };

            if (params) data = _.merge(data, params);

            this.$http.loader('users').put('/api/items/' + this.item.id + '/user', data)
            .then(response =>
            {
                let key = _.findIndex(this.shared.items, {id: this.item.id});

                // replace item
                this.$set(this.shared.items, key, response.data.item);

                // revalidate conflicts
                shared.revalidate(response.data.revalidated);

                // need to update both OLD and NEW user's stats!
                this.updateUserStats(this.item, response.data.stats);
                this.updateUserStats(response.data.item);

                this.$tools.close();
            })
            .catch(error =>
            {
                switch (error.response.data.error)
                {
                    case 'ERR_CONFIRMED':
                        this.$modal.show(this.$t('page.roster.user.error.headline'), this.$t('page.roster.user.error.ERR_CONFIRMED'));
                    break;

                    case 'ERR_OVERLAP':
                        this.$modal.show(this.$t('page.roster.user.error.headline'), user.name + ' ' + this.$t('page.roster.user.error.ERR_OVERLAP'));
                    break;

                    case 'ERR_OPEN_CHECKIN':
                        // item has an open checkin, needs to be checked-out first
                        this.$modal.show(this.$t('page.roster.item.delete.error.headline'), this.$t('page.roster.move.error.ERR_OPEN_CHECKIN'));
                    break;

                    case 'ERR_OVERLAP_REST':
                        // overlap in resting period
                        this.$confirm.show(this.$t('page.roster.user.error.headline'), this.$t('confirm.resting.conflict', {name: user.name}),
                        {
                            confirm: () =>
                            {
                                this.add(user, _.merge({confirmed: 'OVERRULE_REST'}, params)); // user might have confirmed "force" before
                            },
                        });
                    break;

                    case 'ERR_EXISTING_FULL':
                        this.$modal.show(this.$t('page.roster.add.error.headline'), user.name + ' ' + this.$t('page.roster.add.error.ERR_EXISTING_FULL'));
                    break;

                    case 'ERR_UNAVAILABLE_DENY':
                        this.$modal.show(this.$t('page.roster.add.error.headline'), user.name + ' ' + this.$t('page.roster.add.error.ERR_UNAVAILABLE_DENY'));
                    break;

                    case 'ERR_UNAVAILABLE_LIMITED':
                        // user is limited available, schedule anyway?
                        this.$confirm.show(this.$t('page.roster.add.error.headline_limited'), user.name + ' ' + this.$t('page.roster.add.error.ERR_UNAVAILABLE_LIMITED'),
                        {
                            confirm: () =>
                            {
                                this.add(user, _.merge({confirmed: true}, params)); // user might have confirmed "force" before
                            }
                        });
                    break;

                    case 'ERR_HOURS':
                        this.$confirm.show(this.$t('page.roster.user.has_hours.headline'), this.$t('page.roster.user.change.ERR_HOURS'),
                        {
                            confirm: () =>
                            {
                                this.add(user, {force: true});
                            }
                        });
                    break;

                    case 'ERR_UNAVAILABLE_WARN':
                        this.$confirm.show(this.$t('page.roster.add.error.headline'), user.name + ' ' + this.$t('page.roster.add.error.ERR_UNAVAILABLE_WARN'),
                        {
                            confirm: () =>
                            {
                                this.add(user, _.merge({confirmed: true}, params)); // user might have confirmed "force" before
                            }
                        });
                    break;
                }
            });
        },

        remove(user, params)
        {
            this.$http.loader('users').delete('/api/items/' + this.item.id + '/user', params)
            .then(response =>
            {
                let key = _.findIndex(this.shared.items, {id: this.item.id});

                if (response.data.item)
                {
                    // remove user (by substituting assigned item with placeholder item)
                    this.$set(this.shared.items, key, response.data.item);
                }
                else
                {
                    // remove item completely
                    this.$delete(this.shared.items, key);
                }

                // update stats for removed user
                this.updateUserStats(this.item, response.data.stats);

                // revalidate conflicts
                shared.revalidate(response.data.revalidated);

                this.$tools.close();
            })
            .catch(error =>
            {
                switch (error.response.data.error)
                {
                    case 'ERR_HOURS':
                        this.$confirm.show(this.$t('page.roster.user.delete.error.headline'), this.$t('page.roster.user.remove.ERR_HOURS'),
                        {
                            confirm: () =>
                            {
                                this.remove(user, {
                                    params: {
                                        force: true
                                    }
                                });
                            }
                        });
                    break;

                    case 'ERR_CONFIRMED':
                        this.$modal.show(this.$t('page.roster.user.delete.error.headline'), this.$t('page.roster.user.error.ERR_CONFIRMED'));
                    break;

                    case 'ERR_OPEN_CHECKIN':
                        // item has an open checkin, needs to be checked-out first
                        this.$modal.show(this.$t('page.roster.item.delete.error.headline'), this.$t('page.roster.move.error.ERR_OPEN_CHECKIN'));
                    break;
                }
            });
        },

        getAvailability(user)
        {
            let availabilities = this.shared.availability.users[user.id],
                defaultAvailabilities = this.shared.availability.detailedDefaults[user.id];

            // User set availability explicit on date
            if (availabilities && availabilities[this.item.date])
            {
                let availability = _.clone(availabilities[this.item.date]);

                if (availability.constraints) availability.status = 'limited';

                availability.sorting = availabilitySorting.indexOf(availability.type);

                return {
                    explicitAvailable: true,
                    ...(availability),
                    defaultAvailabilities: defaultAvailabilities
                };
            }
            else
            {
                let defaultType = this.shared.availability.defaults[user.id];

                return {
                    explicitAvailable: false,
                    constraints: null,
                    status: null,
                    type: defaultType,
                    sorting: availabilitySorting.indexOf(defaultType),
                    defaultAvailabilities: defaultAvailabilities
                }
            }
        },

        getAvailabilityClasses(user)
        {
            let availability = user.availability.defaultAvailabilities,
                day = moment(this.item.date).isoWeekday() - 1;

            if (user.availability.explicitAvailable) availability = user.availability.type
            else availability = availability ? availability[weekDays[day]].value ? 'available': 'unavailable' : user.availability.type ;

            return [
                'availability',
                'is-' + availability,
                'is-' + user.availability.status
            ];
        },

        showAvailability(user)
        {
            events.$emit('availability.show', user);
        },

        hideAvailability()
        {
            events.$emit('availability.hide');
        },
    },
}
</script>



// WEBPACK FOOTER //
// User.vue?e38b4316